// src/services/authService.js
const BASE_URL = "https://user.globtel.biz/api";
const TIMEOUT_MS = 10000;

function withTimeout(
  promise,
  ms = TIMEOUT_MS,
  controller = new AbortController()
) {
  const timer = setTimeout(() => controller.abort(), ms);
  return promise.finally(() => clearTimeout(timer));
}

export async function registerAuth({ email, password, username }) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(`${BASE_URL}/auth/register`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password, username }),
      signal: controller.signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`Register auth failed: ${res.status} ${text}`);
    }
    return await res.json().catch(() => ({}));
  } finally {
    clearTimeout(timer);
  }
}

export async function sendEmailVerificationCode(email) {
  const controller = new AbortController();
  const url = `${BASE_URL.replace(
    /\/$/,
    ""
  )}/auth/send-email-verification-code?email=${encodeURIComponent(email)}`;
  const res = await withTimeout(
    fetch(url, {
      method: "POST",
      headers: { accept: "application/json" },
      signal: controller.signal,
    })
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Send code failed: ${res.status} ${text}`);
  }
  return res.json(); // { message }
}

export async function verifyEmailCode({ email, code }) {
  const controller = new AbortController();
  const res = await withTimeout(
    fetch(`${BASE_URL}/auth/verify-email-code-without-delete-code`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, code }),
      signal: controller.signal,
    })
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Verify code failed: ${res.status} ${text}`);
  }
  return res.json();
}

export async function login({ email, password, redirect_url }) {
  const controller = new AbortController();
  const res = await withTimeout(
    fetch(`${BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password, redirect_url }),
      credentials: "include",
      signal: controller.signal,
    })
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Login failed (${res.status})`);
  }
  return res.json();
}

export async function checkToken(token) {
  const controller = new AbortController();
  const res = await withTimeout(
    fetch(`${BASE_URL}/auth/check-token`, {
      method: "GET",
      headers: { accept: "application/json", Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Token validation failed (${res.status})`);
  }
  return res.json();
}

export async function sendResetCode(email) {
  const controller = new AbortController();
  const res = await withTimeout(
    fetch(
      `${BASE_URL}/auth/send-password-reset-code?email=${encodeURIComponent(
        email
      )}`,
      {
        method: "POST",
        headers: { accept: "application/json" },
        signal: controller.signal,
      }
    )
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Failed to send reset code (${res.status})`);
  }
  return res.json();
}

export async function resetPassword({ email, code, new_password }) {
  const controller = new AbortController();
  const res = await withTimeout(
    fetch(`${BASE_URL}/auth/update-password`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, code, new_password }),
      signal: controller.signal,
    })
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Failed to reset password (${res.status})`);
  }
  return res.json();
}
